import { Cpu, ShieldCheck } from "lucide-react";
import { useCallback, useEffect, useState } from "react";

import { Badge } from "@/client/components/ui/badge";
import { Button } from "@/client/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/client/components/ui/card";
import { Input } from "@/client/components/ui/input";
import { type BmcPolicy, bmcApi } from "@/client/features/bmc/bmc-api";
import { ApiError } from "@/client/lib/api";

/**
 * Staff-only BMC transparency panels (PE-650), rendered only while a Mantle staff
 * member is viewing a tenant ("view as"). The Redfish explorer and policy view
 * are operational/debug tooling, not customer surface; the server also enforces
 * the staff-only gate so this can't be reached by a customer.
 */
export function BmcStaffPanels({ nodeId }: { nodeId: string }) {
  return (
    <div className="flex flex-col gap-6">
      <RedfishExplorer nodeId={nodeId} />
      <PolicyPanel nodeId={nodeId} />
    </div>
  );
}

export function RedfishExplorer({ nodeId }: { nodeId: string }) {
  const [path, setPath] = useState("/redfish/v1/");
  const [result, setResult] = useState("");
  const [loading, setLoading] = useState(false);

  const fetchPath = async () => {
    setLoading(true);
    try {
      const { status, body } = await bmcApi.redfish(nodeId, path);
      // Pretty-print JSON when we can; otherwise show the raw body verbatim.
      let pretty = body;
      try {
        pretty = JSON.stringify(JSON.parse(body), null, 2);
      } catch {
        // not JSON; leave as-is
      }
      setResult(`${status}\n${pretty}`);
    } catch (err) {
      setResult(err instanceof ApiError ? `${err.status} ${err.message}` : "Request failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <Cpu className="text-muted-foreground size-4" />
          <CardTitle>Redfish explorer</CardTitle>
          <Badge variant="outline" className="font-normal">
            Staff
          </Badge>
        </div>
        <CardDescription>
          GET passthrough for allowed Redfish paths. The proxy's default-deny policy still applies.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        <div className="flex gap-2">
          <Input
            value={path}
            onChange={(e) => setPath(e.target.value)}
            spellCheck={false}
            className="font-mono text-sm"
          />
          <Button variant="outline" onClick={() => void fetchPath()} disabled={loading}>
            {loading ? "Fetching…" : "GET"}
          </Button>
        </div>
        {result ? (
          <pre className="bg-muted max-h-96 overflow-auto rounded-md p-3 font-mono text-xs">
            {result}
          </pre>
        ) : null}
      </CardContent>
    </Card>
  );
}

type PolicyState = "loading" | "ready" | "error";

export function PolicyPanel({ nodeId }: { nodeId: string }) {
  const [policy, setPolicy] = useState<BmcPolicy | null>(null);
  const [state, setState] = useState<PolicyState>("loading");

  const load = useCallback(async () => {
    setState("loading");
    try {
      setPolicy(await bmcApi.policy(nodeId));
      setState("ready");
    } catch {
      setState("error");
    }
  }, [nodeId]);

  useEffect(() => {
    void load();
  }, [load]);

  const rules = policy?.rules ?? [];
  const allowCount = rules.filter((r) => r.effect === "allow").length;
  const denyCount = rules.filter((r) => r.effect === "deny").length;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <ShieldCheck className="text-muted-foreground size-4" />
          <CardTitle>Access policy</CardTitle>
          <Badge variant="outline" className="font-normal">
            Staff
          </Badge>
        </div>
        <CardDescription>
          The default-deny allow/deny rules the proxy enforces for this node.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {state === "loading" ? (
          <p className="text-muted-foreground text-sm">Loading policy…</p>
        ) : state === "error" ? (
          <p className="text-muted-foreground text-sm">
            Couldn't load the policy.{" "}
            <button type="button" className="underline" onClick={() => void load()}>
              Retry
            </button>
            .
          </p>
        ) : policy ? (
          <>
            <div className="flex flex-wrap gap-2">
              <Badge variant="outline">default {policy.default}</Badge>
              <Badge variant="secondary">{allowCount} allow</Badge>
              <Badge variant="destructive">{denyCount} deny</Badge>
            </div>
            <ul className="flex flex-col divide-y">
              {rules.map((rule) => (
                <li key={rule.name} className="flex items-start gap-3 py-2 first:pt-0 last:pb-0">
                  <Badge variant={rule.effect === "allow" ? "secondary" : "destructive"}>
                    {rule.effect}
                  </Badge>
                  <div className="min-w-0">
                    <p className="text-sm font-medium">{rule.name}</p>
                    <p className="text-muted-foreground font-mono text-xs break-all">
                      {(rule.methods ?? ["*"]).join(", ")}{" "}
                      {rule.path ?? rule.pathPrefix ?? rule.pathPattern ?? ""}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          </>
        ) : null}
      </CardContent>
    </Card>
  );
}
